import type {ReactElement} from 'react';
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isUserLoggedIn} from '@Libraries/user.library';
import {MicState} from '@AiChat/hooks/useAudioLimit';
import {getMicTooltip, callStartRecording} from '@Components/ai-prompt-box/libraries/ai-prompt-box-mic.library';
import type {RootState} from '@/store';
import styles from './ai-record-audio-button.module.scss';
import actionButtonStyles from '../../ai-prompt-box-action-button.module.scss';

const SIZE_CLASS_MAP = {
  [AiPromptBoxSize.SMALL]: actionButtonStyles.actionButtonSmall,
  [AiPromptBoxSize.MEDIUM]: actionButtonStyles.actionButtonMedium,
  [AiPromptBoxSize.SINGLE_LINE]: actionButtonStyles.actionButtonSingleLine,
  [AiPromptBoxSize.SMALL_EXTENDED]: actionButtonStyles.actionButtonSmall,
};

const SIZE_ICON_SIZE_MAP = {
  [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_16,
  [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_20,
  [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_16,
  [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_16,
};

interface AiRecordAudioButtonProps {
  id: string;
}

export function AiRecordAudioButton({id}: AiRecordAudioButtonProps): ReactElement {
  const {state} = useAiPromptBox(id);
  const audioLimit = useSelector((s: RootState) => s.aiChat.audioLimit);
  const isFetchingAudioLimit = useSelector((s: RootState) => s.aiChat.isFetchingAudioLimit);

  const micState: MicState = useMemo(() => {
    if (isFetchingAudioLimit || audioLimit.lastSyncedAt === null) {
      return MicState.LOADING;
    }
    if (audioLimit.fetchError) {
      return MicState.ERROR;
    }
    if (audioLimit.isLimitReached) {
      return MicState.LIMITED;
    }
    return MicState.READY;
  }, [isFetchingAudioLimit, audioLimit.lastSyncedAt, audioLimit.fetchError, audioLimit.isLimitReached]);

  const permissionDenied = state.transcription?.permissionDenied ?? false;
  const disabled = permissionDenied || (isUserLoggedIn() && micState !== MicState.READY);
  const tooltip = getMicTooltip(micState, permissionDenied);

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    callStartRecording(id);
  };

  return (
    <ClickableDiv className={`${styles.micButtonWrapper} ${SIZE_CLASS_MAP[state.size]} radius-round flex-center ${disabled ? styles.disabled : ''}`} onClick={handleClick}>
      <span className={styles.tooltip}>{tooltip}</span>
      <Icon icon="icon-microphone" size={SIZE_ICON_SIZE_MAP[state.size]} />
    </ClickableDiv>
  );
}
